import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { UnitInput } from "@/components/ui/unit-input";
import { api, errorMessage } from "@/services/api";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";

/**
 * How many supplementary accounts a primary of each member type may carry.
 *
 * One row per primary account type, read and written one at a time:
 * `GET /config/supplementary-limits` brings every type with the limit in force,
 * and `PUT /config/supplementary-limits/{id}` sets one of them. The API refuses
 * a limit below the slots a primary of that type already occupies, so a save
 * can come back rejected on a number that looked perfectly reasonable — the
 * message it sends is what the person sees.
 */

interface Limit {
  id: number;
  /** The canonical key the resolver hangs off — `gold_member` — or null if unknown. */
  config_key: string | null;
  /** The free-text label as configured. */
  member_type: string | null;
  /** The limit in force: what was configured, clamped to the ceiling. */
  max_supplementary: number;
  configured_limit: number | null;
  is_clamped?: boolean;
  is_fallback?: boolean;
}

interface Screen {
  total: number;
  /** Highest value a limit may take; anything above it is clamped on read. */
  max_configurable_limit: number;
  data: Limit[];
}

const SCREEN_KEY = ["/config/supplementary-limits"] as const;

/**
 * The documented fallback, which the API applies when a row has no configured
 * value but does not itself return as a field. Shown as what the type would
 * fall back to, never as what it currently is.
 */
const DOCUMENTED_DEFAULT = 2;

/** `gold_complimentary` reads as a database key; "Gold Complimentary" reads as a type. */
const titleCase = (key: string) =>
  key
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export function SupplementaryLimits() {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  // The API refuses a write from an account without the permission; the box
  // says so before the save does, rather than taking a number it cannot keep.
  const editable = allow("change__supplementary_limit");

  const screen = useQuery({
    queryKey: SCREEN_KEY,
    queryFn: async ({ signal }) =>
      (await api.get<Screen>("/config/supplementary-limits", { signal })).data,
  });
  const rows = screen.data?.data ?? [];
  const ceiling = screen.data?.max_configurable_limit;

  /**
   * The change waiting on an answer: which type, and what it would become.
   *
   * A box does not save itself. Leaving it with a new number asks first, as it
   * does on the idle time and password expiry screens — the number here is a
   * ceiling on what members may hold, and lowering one is not a keystroke.
   */
  const [pending, setPending] = useState<{ id: number; name: string; value: number } | null>(
    null,
  );
  /** Bumped when a change is refused: every box goes back to what the API holds. */
  const [reverted, setReverted] = useState(0);

  const refuse = () => {
    setPending(null);
    setReverted((n) => n + 1);
  };

  const save = useMutation({
    mutationFn: async ({ id, value }: { id: number; value: number }) =>
      (
        await api.put<{ data: Limit }>(`/config/supplementary-limits/${id}`, {
          max_supplementary: value,
        })
      ).data,
    onSuccess: () => {
      toast.success(t("common.saved"));
      void queryClient.invalidateQueries({ queryKey: SCREEN_KEY });
      setPending(null);
    },
    onError: (error) => {
      toast.error(errorMessage(error, t("common.couldNotSave")));
      // Refused by the server is still refused: the box goes back.
      refuse();
    },
  });

  return (
    <div>
      <h2 className="text-title-2 text-heading">{t("supp.title")}</h2>
      <p className="text-fg-muted text-callout mt-1">{t("supp.text")}</p>

      <h3 className="text-heading text-callout mt-5 font-medium">{t("supp.primaryTypes")}</h3>

      <div className="mt-3 grid gap-3">
        {screen.isPending
          ? Array.from({ length: 3 }, (_, index) => (
              <div
                key={index}
                className="bg-surface-raised rounded-card flex items-center justify-between gap-6 px-5 py-4 shadow-[var(--shadow-lift)]"
              >
                <div className="grid min-w-0 flex-1 basis-64 gap-2">
                  <Skeleton className="w-40 max-w-full" />
                  <Skeleton className="h-3 w-64 max-w-full" />
                </div>
                <Skeleton className="h-9 w-28 shrink-0 rounded-lg" />
              </div>
            ))
          : null}

        {rows.map((row) => (
          <div
            key={row.id}
            className="bg-surface-raised rounded-card shadow-[var(--shadow-lift)]"
          >
            <TypeRow
              title={label(row)}
              text={t("supp.default").replace("{n}", String(DOCUMENTED_DEFAULT))}
              unit={t("supp.accounts")}
              value={row.max_supplementary}
              max={ceiling}
              reverted={reverted}
              busy={save.isPending || !editable}
              onCommit={(value) => setPending({ id: row.id, name: label(row), value })}
            />
          </div>
        ))}

        {!screen.isPending && rows.length === 0 ? (
          <p className="text-fg-muted text-callout py-6 text-center">{t("supp.none")}</p>
        ) : null}
      </div>

      {/* The question a box asks on its way out: taken, or put back. */}
      <ConfirmDialog
        open={pending !== null}
        onClose={refuse}
        onConfirm={() =>
          pending !== null && save.mutate({ id: pending.id, value: pending.value })
        }
        tone="warning"
        title={t("supp.updateTitle")}
        message={
          pending === null
            ? ""
            : t("supp.updateAsk")
                .replace("{name}", pending.name)
                .replace("{value}", String(pending.value))
                .replace("{unit}", t("supp.accounts").toLowerCase())
        }
        confirmLabel={t("common.update")}
        cancelLabel={t("common.cancel")}
        loading={save.isPending}
      />
    </div>
  );
}

/** What to call the row: its own label, failing that its key, failing that its id. */
function label(row: Limit): string {
  if (row.member_type) return row.member_type;
  if (row.config_key) return titleCase(row.config_key);
  return `#${row.id}`;
}

/**
 * One account type: who it applies to, and how many accounts it allows.
 *
 * The box commits on blur or Enter, and only when the number actually changed —
 * tabbing through a card is not an edit. An emptied box is not a limit of
 * nothing, so it goes back to what it held.
 *
 * Enter asks the question where the box stands rather than blurring to get
 * there. Blurring works — the question is the same one — but it drops focus on
 * the way, so the dialog has nowhere to hand it back to and answering from the
 * keyboard leaves the caret at the top of the page instead of in the box that
 * was just typed in.
 *
 * The ceiling is held here as well as by the API. `max` on a number input only
 * governs the spinner arrows and form validation the screen never runs, so a
 * typed number sails past it: without this check a stray digit reaches the
 * server and comes back a 422 the person has to translate for themselves. The
 * ceiling comes from `max_configurable_limit` rather than a constant of our
 * own, so the two are the same number by construction — and when the response
 * carries none, nothing is refused here and the API stays the only judge.
 */
function TypeRow({
  title,
  text,
  unit,
  value,
  max,
  reverted,
  busy,
  onCommit,
}: {
  title: string;
  text: string;
  unit: string;
  value: number;
  max?: number | undefined;
  /** Changes when a save was refused, which puts the box back. */
  reverted: number;
  busy: boolean;
  onCommit: (value: number) => void;
}) {
  const t = useTranslation();
  const [draft, setDraft] = useState(String(value));
  const [seen, setSeen] = useState(value);
  const [seenRevert, setSeenRevert] = useState(reverted);
  // The number already put to the person. Opening the question takes focus out
  // of the box, and the blur that follows must not ask the same thing twice.
  // Cleared by the next keystroke, which is the only way a new number arrives.
  const asked = useRef<number | null>(null);
  // A save elsewhere moves the truth; follow it without an effect. A refused
  // save moves nothing, so the box is put back by hand.
  if (seen !== value || seenRevert !== reverted) {
    setSeen(value);
    setSeenRevert(reverted);
    setDraft(String(value));
  }

  const commit = () => {
    const next = Number(draft.trim());
    if (draft.trim() === "" || Number.isNaN(next) || next < 0) {
      setDraft(String(value));
      return;
    }
    // Over the ceiling is said out loud rather than put back quietly: the
    // number was deliberate, and a box that silently snaps back reads as a
    // screen that dropped a keystroke.
    if (max !== undefined && next > max) {
      toast.error(t("supp.ceiling").replace("{n}", String(max)));
      setDraft(String(value));
      return;
    }
    if (next === value || asked.current === next) return;
    asked.current = next;
    onCommit(next);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-4">
      <div className="min-w-0 flex-1 basis-64">
        <p className="text-heading text-callout font-semibold break-words">{title}</p>
        <p className="text-fg-muted text-subhead mt-0.5 leading-[18px] break-words">{text}</p>
      </div>
      <label className="text-callout ml-auto shrink-0">
        <span className="sr-only">{`${title}: ${unit}`}</span>
        <UnitInput
          unit={unit}
          type="number"
          min={0}
          max={max}
          inputMode="numeric"
          disabled={busy}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            asked.current = null;
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            commit();
          }}
          // Sized to the number it holds — two digits — rather than to the
          // card it sits in, as the idle time and expiry boxes are.
          className="h-9 w-auto"
          inputClassName="w-16 flex-none px-2 text-center"
        />
      </label>
    </div>
  );
}
